import { ExtractionConfidence } from '@common/types';
import { Box, Tooltip, Typography } from '@common-components';
import { Circle } from '@icons';
import { messages } from 'i18n';

const CONFIDENCE_BANDS = [
  { min: 75, color: 'success.main' },
  { min: 50, color: 'warning.main' },
  { min: 0, color: 'error.main' },
] as const;

function getScoreColor(score: number): string {
  return CONFIDENCE_BANDS.find((band) => score >= band.min)?.color ?? 'error.main';
}

interface ConfidenceScoreProps {
  confidence?: ExtractionConfidence | null;
}

export function ConfidenceScore({ confidence }: ConfidenceScoreProps) {
  const score = confidence?.score;
  if (score == null) {
    return null;
  }

  const color = getScoreColor(score);

  const reason = confidence?.reason;
  const tooltipContent = reason ? (
    <Box sx={{ textAlign: 'left' }}>
      <Typography variant="body2">{messages.general.extractedByFlow(score)}</Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        <Box component="span" fontWeight={600}>
          {messages.general.extractedByFlowReasonLabel}{' '}
        </Box>
        {reason}
      </Typography>
    </Box>
  ) : (
    messages.general.extractedByFlow(score)
  );

  return (
    <Tooltip tooltipContent={tooltipContent}>
      <Box display="flex" alignItems="center" gap={0.5} data-testid="confidence-score">
        <Circle sx={{ width: 8, height: 8, color }} />
        <Typography variant="caption" fontWeight={600} color={color} whiteSpace="nowrap">
          {score}%
        </Typography>
      </Box>
    </Tooltip>
  );
}
